/* @flow */

import * as React from 'react';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps, TFunction } from 'react-i18next';
import moment from 'moment';
import Moment from 'react-moment';
import NumberFormat from 'react-number-format';
import type { RouteComponentProps } from 'react-router-dom';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Paper from '@material-ui/core/Paper';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

import { getUserSeparatorValues, getCurrencySymbol, getRate } from 'utils/getCurrencySymbol';
// Load stores
import LocationDetails from 'components/Locations/LocationDetails/LocationDetails';
import type { TypeAsset } from 'stores/AssetStore';
import ActionDrawerStore from 'stores/ActionDrawerStore';
// Load global components
import Loading from 'components/Loading/Loading';

import AdditionalInfo from 'components/AdditionalInfo/AdditionalInfo';
import AssignedTo from 'components/AssignedTo';
import AuditAssetStatus from 'components/AuditAssetStatus';
import ActionsList from 'components/ActionsList/ActionsList';
import StatusIndicator from 'components/StatusIndicator/StatusIndicator';
import PageTitle from 'components/PageTitleLegacy/PageTitle';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import ActionDrawerWrapper from 'components/ActionDrawerWrapper/ActionDrawerWrapper';
import Activity from 'components/Activity/Activity';
import Availability from 'components/Availability/Availability';
import type { TypeAvailability } from 'components/Availability/Availability';
import CustomFields from 'components/CustomFields/CustomFields';
import * as statuses from 'utils/statuses';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import DeliveryDetails from './components/DeliveryDetails/DeliveryDetails';

import {
  AllocateAssetToProject,
  ChangeAssetLocation,
  ExtendRental,
  UnassignUserFromAsset,
  ReassignAsset,
  RemoveAssetFromProject,
  ReportAssetFault,
  RequestAssetQuote,
  RequestAssetOwnership,
  ScheduleAssetCalibration,
  ArchiveAsset,
  UnarchiveAsset,
  SelfAuditMulti,
} from 'views/ActionDrawer';

import styles from 'common-scss/DetailsPage.module.scss';

import ProjectDetails from './components/ProjectDetails/ProjectDetails';
import ContractDetails from './components/ContractDetails/ContractDetails';
import FinancialAudit from './components/FinancialAudit/FinancialAudit';
import Documents from './components/Documents/Documents';
import AssetAccessories from './components/AssetAccessories/AssetAccessories';
import AssetRelationship from './components/AssetRelationship/AssetRelationship';
import AssetNotepad from './components/AssetNotepad';

import AssetStore from '../../stores/AssetStore';

import { actionsListDataOwned, actionsListDataRented } from './actionsListData';
import OptionsFitted from './components/OptionsFitted/OptionsFitted';
import ExchangeDetails from './components/ExchangeDetails/ExchangeDetails';
import { generateUrl, asset as assetUrls } from 'api/routes';
import { ElectroTabs } from '../../components/ExtendedUiElements/customized';

type CustomFieldType = {
  label: string,
  value: string,
  custom_value: string,
};

type InjectedProps = {
  assetStore: AssetStore,
  actionDrawerStore: ActionDrawerStore,
} & RouteComponentProps &
  TranslatorProps;

function customFields(t, label) {
  const fields = {
    'CustomField 1': t('general:labels.custom_field', { fieldNumber: 1 }),
    'CustomField 2': t('general:labels.custom_field', { fieldNumber: 2 }),
    'CustomField 3': t('general:labels.custom_field', { fieldNumber: 3 }),
    'CustomField 4': t('general:labels.custom_field', { fieldNumber: 4 }),
  };

  if (Object.keys(fields).includes(label)) {
    return fields[label];
  }

  return label;
}

@translate()
@inject('assetStore', 'actionDrawerStore', 'navigationStore')
@observer
export default class AssetDetailsPage extends ComponentWithInjectedProps<{}, {}, InjectedProps> {
  @observable activeTab: Object = {
    rentedActiveTab: 'contract',
    ownedActiveTab: 'project',
  };

  componentDidMount() {
    const { assetStore, match }: { assetStore: AssetStore, match: Object } = this.props;

    assetStore.loadAsset(match.params.id, false);
  }

  componentWillUnmount() {
    const { assetStore, actionDrawerStore, match } = this.props;

    if (!match.path.includes('/assets/:id')) {
      actionDrawerStore.clearActionDrawer();
      assetStore.clearAssetStore();
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { assetStore, match } = this.props;

    if (assetStore.asset && assetStore.asset.id !== match.params.id && !assetStore.loadingAsset) {
      assetStore.loadAsset(match.params.id, true);
    }

    if (prevProps.actionDrawerStore.loadParent) {
      assetStore.loadAsset(match.params.id, true);
      this.props.actionDrawerStore.loadParent = false;
    }
  }

  handleTabChange = (tabType: string) => (ev: SyntheticInputEvent<*>, value: string) => {
    this.activeTab = {
      ...this.activeTab,
      [tabType]: value,
    };
  };

  renderPricingFields = (asset: TypeAsset) => {
    const { t } = this.props;
    const { thousandSeparator, decimalSeparator } = getUserSeparatorValues();

    switch (asset.pricing.model) {
      case 'PERFECT':
        return (
          <table>
            <tbody>
              {!!asset.pricing.transaction_type_description && (
                <tr>
                  <td>
                    <strong>{t('general:assets.transaction_type')}:</strong>
                  </td>
                  <td>{asset.pricing.transaction_type_description}</td>
                </tr>
              )}
              <tr>
                <td>
                  <strong>
                    {statuses.getCode(asset.status) === 'sold'
                      ? t('general:assets.labels.price')
                      : t('general:assets.labels.rate')}
                    :
                  </strong>
                </td>
                <td>
                  <NumberFormat
                    value={asset.pricing.rate || 0}
                    displayType="text"
                    prefix={
                      getCurrencySymbol(
                        asset.pricing && asset.pricing.currency,
                        (asset.contract && asset.contract.er_currency) || ''
                      ) + ' '
                    }
                    decimalScale={2}
                    thousandSeparator={thousandSeparator}
                    decimalSeparator={decimalSeparator}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        );

      case 'PERFECT_SALE':
        return (
          <table>
            <tbody>
              <tr>
                <td>
                  <strong>{t('general:assets.transaction_type')}:</strong>
                </td>
                <td>{asset.pricing.transaction_type_description}</td>
              </tr>
              <tr>
                <td>
                  <strong>{t('general:assets.labels.price')}:</strong>
                </td>
                <td>
                  <NumberFormat
                    value={asset.pricing.sale_rate || 0}
                    displayType="text"
                    prefix={
                      getCurrencySymbol(
                        asset.pricing && asset.pricing.currency,
                        (asset.contract && asset.contract.er_currency) || ''
                      ) + ' '
                    }
                    decimalScale={2}
                    thousandSeparator={thousandSeparator}
                    decimalSeparator={decimalSeparator}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        );

      case 'D365':
        return (
          <table>
            <tbody>
              <tr>
                <td>
                  <strong>{t('general:assets.transaction_type')}:</strong>
                </td>
                <td>{asset.pricing.transaction_type_description}</td>
              </tr>
              <tr>
                <td>
                  <strong>
                    {statuses.getCode(asset.status) === 'sold'
                      ? t('general:assets.labels.price')
                      : t('general:assets.labels.rate')}
                    :
                  </strong>
                </td>
                <td>
                  <NumberFormat
                    value={asset.pricing.rate || 0}
                    displayType="text"
                    prefix={
                      getCurrencySymbol(
                        asset.pricing && asset.pricing.currency,
                        (asset.contract && asset.contract.er_currency) || ''
                      ) + ' '
                    }
                    decimalScale={2}
                    thousandSeparator={thousandSeparator}
                    decimalSeparator={decimalSeparator}
                  />{' '}
                  {statuses.getCode(asset.status) !== 'sold' &&
                    getRate(asset.pricing.rate_code, ' Rate N/A')}
                </td>
              </tr>
            </tbody>
          </table>
        );
    }
  };

  renderOwnedAssetTabs = (t: TFunction) => {
    const { asset } = this.props.assetStore;

    return (
      <div className={styles['detail-tab-group']}>
        <Tabs
          className={styles['detail-tabs']}
          value={this.activeTab.ownedActiveTab}
          onChange={this.handleTabChange('ownedActiveTab')}
        >
          <Tab label={t('general:project')} value="project" />
          <Tab label={t('general:assets.labels.financial_audit')} value="financial" />
          <Tab label={t('general:labels.documents_resources')} value="documents" />
          {asset.calibration_service && (
            <Tab label={t('general:assets.labels.asset_notepad')} value="assetNotepad" />
          )}
        </Tabs>

        <div className={styles['detail-tabbed-area']}>
          {
            {
              project: <ProjectDetails />,
              financial: <FinancialAudit />,
              documents: (
                <Documents
                  title={t('general:calibrations.certificate_plural')}
                  documentType={'asset'}
                />
              ),
              assetNotepad: <AssetNotepad />,
            }[this.activeTab.ownedActiveTab]
          }
        </div>
      </div>
    );
  };

  renderRentedAssetTabs = (t: TFunction, asset: TypeAsset) => {
    return (
      <div className={styles['detail-tab-group']}>
        <ElectroTabs
          className={styles['detail-tabs']}
          value={this.activeTab.rentedActiveTab}
          onChange={this.handleTabChange('rentedActiveTab')}
          variant="scrollable"
          scrollButtons="on"
        >
          <Tab label={t('general:contract')} value="contract" />
          <Tab label={t('general:project')} value="project" />
          <Tab label={t('general:labels.documents_resources')} value="documents" />
          <Tab label={t('general:assets.labels.accessories')} value="accessories" />
          <Tab label={t('general:assets.labels.options_fitted')} value="optionsFitted" />
          <Tab
            label={
              asset.replacement && asset.replacement.is_its_own_rpl
                ? t('general:assets.labels.calibrated_asset_to_fit_your_needs')
                : t('general:assets.labels.exchange_details')
            }
            value="exchangeDetails"
            disabled={asset.replacement == null}
          />
          <Tab label={t('general:assets.labels.asset_notepad')} value="assetNotepad" />
        </ElectroTabs>

        <div className={styles['detail-tabbed-area']}>
          {
            {
              contract: <ContractDetails />,
              project: <ProjectDetails />,
              documents: (
                <Documents
                  title={t('general:calibrations.certificate_plural')}
                  documentType={'asset'}
                />
              ),
              accessories: <AssetAccessories />,
              optionsFitted: <OptionsFitted options={asset.options_fitted} />,
              exchangeDetails: <ExchangeDetails options={asset.replacement} />,
              assetNotepad: <AssetNotepad />,
            }[this.activeTab.rentedActiveTab]
          }
        </div>
      </div>
    );
  };

  render() {
    const { assetStore, t, actionDrawerStore: adStore } = this.props;
    const { asset } = this.props.assetStore;

    if (assetStore.loadingAsset || !asset) {
      return (
        <PageWrapper>
          <Loading />
        </PageWrapper>
      );
    }

    const actionsListData: Array<*> =
      asset && asset.owned ? actionsListDataOwned : actionsListDataRented;
    const isSold = statuses.getCode(asset.status) === 'sold';
    const assetContractType = asset.owned ? 'own' : isSold ? 'sold' : 'rented';
    const title = `${t('general:asset')} | ${asset.asset_number} - ${
      asset.model
    } - ${statuses.translate(assetContractType, this.props.i18n)}`;
    const isLease = asset.pricing
      ? asset.pricing.transaction_type_description === 'Lease Only'
      : false;
    const isOnRent = asset.pricing
      ? asset.pricing.transaction_type_description === 'Rental'
      : false;

    asset.disabled = this.props.disabled || asset.status === 'closed';

    const ownedAssetAvailability: TypeAvailability[] = [
      {
        id: 1,
        label: t('general:assets.labels.sale_consignment'),
        content: asset.sale_consignment,
      },
      {
        id: 2,
        label: t('general:assets.labels.sub_rental_program'),
        content: asset.subrental_program,
      },
    ];
    const calCer = assetStore.getCalibrationCer;

    // Price formatting separators, checks for 1,234,567.89
    // This will need revisiting if any other formats are added
    const { thousandSeparator, decimalSeparator } = getUserSeparatorValues();
    const environment = process.env.REACT_APP_SHORT_NAME; // Either "ER" or "ML".
    const isEnvironmentML = environment === 'ML';

    const orderDatesRows: React.Node[] = [
      isSold && (
        <tr key="orderDateRow-purchasedDate">
          <td>
            <strong>{t('general:assets.purchased_date')}:</strong>
          </td>
          <td>
            {asset.er_delivery_date && moment(asset.er_delivery_date).format('MMMM DD, YYYY')}
          </td>
        </tr>
      ),
      (!isSold || isLease || isOnRent) && (
        <tr key="orderDateRow-onRentDate">
          <td>
            <strong>{t('general:assets.on_rent_date')}:</strong>
          </td>
          <td>
            {asset.er_hire_start_date && moment(asset.er_hire_start_date).format('MMMM DD, YYYY')}
          </td>
        </tr>
      ),
      (!isSold || isOnRent) && (
        <tr key="orderDateRow-expectedOffRentDate">
          <td>
            <strong>{t('general:assets.exp_off_rent_date')}:</strong>
          </td>
          <td>
            {asset.er_expected_hire_end_date &&
              moment(asset.er_expected_hire_end_date).format('MMMM DD, YYYY')}
          </td>
        </tr>
      ),
      (!isSold || isLease || isOnRent) && (
        <React.Fragment>
          {asset.er_hire_end_date && (
            <tr key="orderDateRow-returnedDate">
              <td>
                <strong>{t('general:assets.returned_date')}:</strong>
              </td>
              <td>{moment(asset.er_hire_end_date).format('MMMM DD, YYYY')}</td>
            </tr>
          )}
        </React.Fragment>
      ),
    ].filter(row => row);

    return (
      <React.Fragment>
        <PageWrapper isFlex={false}>
          <PageTitle title={title} />

          <div className={styles['grid-wrapper']}>
            <Paper className={styles['feature-item']}>
              <div className={styles['details-panel']}>
                <h3>{t(`general:assets.labels.details`)}</h3>
                <table className={styles['basic-table']}>
                  <tbody>
                    <tr>
                      <td>
                        <strong>{t('general:trading_accounts.labels.name')}:</strong>
                      </td>
                      <td>{asset.account.name}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>{t('general:trading_accounts.labels.number')}:</strong>
                      </td>
                      <td>{asset.account.number}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>{t('general:assets.labels.number')}:</strong>
                      </td>
                      <td>{asset.asset_number}</td>
                    </tr>
                    {asset.owned && (
                      <tr>
                        <td>
                          <strong>{t('general:labels.barcode_number')}:</strong>
                        </td>
                        <td>{asset.barcode_number}</td>
                      </tr>
                    )}
                    <tr>
                      <td>
                        <strong>{t('general:manufacturer')}:</strong>
                      </td>
                      <td>{asset.manufacturer}</td>
                    </tr>
                    {!asset.owned && (
                      <tr>
                        <td>
                          <strong>{t('general:assets.labels.ml_product_type')}:</strong>
                        </td>
                        <td>{asset.er_product_type}</td>
                      </tr>
                    )}
                    <tr>
                      <td>
                        <strong>{t('general:model')}:</strong>
                      </td>
                      <td>{asset.model}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>{t('general:description')}:</strong>
                      </td>
                      <td>{asset.description}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>{t('general:assets.labels.options_fitted')}:</strong>
                      </td>
                      <td>
                        {/* Rented Asset */} {/* Owned Asset */}
                        {asset.er_options_fitted_string || asset.owned_options_fitted_text}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>{t('general:labels.serial_number')}:</strong>
                      </td>
                      <td>{asset.serial_number}</td>
                    </tr>
                    {asset.owned && (
                      <React.Fragment>
                        <tr>
                          <td>
                            <strong>{t('general:assets.labels.relationship_status')}:</strong>
                          </td>
                          <td>{asset.asset_relationship}</td>
                        </tr>
                      </React.Fragment>
                    )}
                    {!asset.owned && isEnvironmentML && (
                      <tr>
                        <td>
                          <strong>{t('general:contracts.labels.asset_status')}:</strong>
                        </td>
                        <td>{asset.contract_asset_status}</td>
                      </tr>
                    )}
                    {asset.owned && (
                      <React.Fragment>
                        <tr>
                          <td>
                            <strong>{t('general:assets.labels.software_version')}:</strong>
                          </td>
                          <td>{asset.software_version}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>{t('general:assets.labels.firmware_version')}:</strong>
                          </td>
                          <td>{asset.firmware_version}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>{t('general:assets.labels.current_condition')}:</strong>
                          </td>
                          <td>{asset.current_condition}</td>
                        </tr>
                      </React.Fragment>
                    )}
                  </tbody>
                </table>

                {asset.custom_fields && !!asset.custom_fields.length && (
                  <CustomFields
                    title={t('general:assets.labels.custom_fields')}
                    customFields={asset.custom_fields}
                  />
                )}

                {asset.is_caas_asset && asset.calibration_service && (
                  <>
                    <h3>{t('general:calibration_as_a_service')}</h3>
                    <table>
                      <tbody>
                        <tr>
                          <td>
                            <strong>{t('general:calibrations.labels.work_order_number')}:</strong>
                          </td>
                          <td>{asset.calibration_service.work_order_number}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>{t('general:calibrations.labels.rental_backfill')}:</strong>
                          </td>
                          <td>{asset.calibration_service.is_rental_backfill ? 'Yes' : 'No'}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>{t('general:vendor')}:</strong>
                          </td>
                          <td>{asset.calibration_service.vendor}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>{t('general:calibrations.labels.type')}:</strong>
                          </td>
                          <td>{asset.calibration_service.type}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>{t('general:calibrations.labels.currency')}:</strong>
                          </td>
                          <td>{asset.calibration_service.currency}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>{t('general:calibrations.labels.price')}:</strong>
                          </td>
                          <td>
                            <NumberFormat
                              decimalScale={2}
                              displayType="text"
                              decimalSeparator="."
                              thousandSeparator=","
                              value={asset.calibration_service.price}
                              prefix={getCurrencySymbol(asset.calibration_service.currency)}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>{t('general:calibrations.labels.status')}:</strong>
                          </td>
                          <td>{asset.calibration_service.status}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>{t('general:observations')}:</strong>
                          </td>
                          <td>{asset.calibration_service.observations}</td>
                        </tr>
                      </tbody>
                    </table>
                  </>
                )}

                <h3>{t('general:calibrations.labels.details')}</h3>
                <table>
                  <tbody>
                    {asset.calibration.status !== 'Not Required' && (
                      <React.Fragment>
                        <tr>
                          <td>
                            <strong>{t('general:calibrations.certificate')}:</strong>
                          </td>
                          {calCer.length > 0 ? (
                            <td>
                              <a
                                className={styles.link}
                                onClick={() =>
                                  adStore.exportPdfFile(calCer[0].link, calCer[0].name)
                                }
                              >
                                {calCer[0].name}
                              </a>
                            </td>
                          ) : (
                            <td> - </td>
                          )}
                        </tr>
                        <tr>
                          <td>
                            <strong>{t('general:calibrations.labels.last_calibrated')}:</strong>
                          </td>
                          <td>
                            {asset.calibration.date && (
                              <Moment date={asset.calibration.date} format="MMMM DD, YYYY" />
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>{t('general:calibrations.labels.due_date')}:</strong>
                          </td>
                          <td>
                            {asset.calibration.due_date && (
                              <React.Fragment>
                                <Moment date={asset.calibration.due_date} format="MMMM DD, YYYY" />
                                {(function() {
                                  const calDueDate = moment(asset.calibration.due_date).diff(
                                    moment(),
                                    'days'
                                  );

                                  return (
                                    <span>
                                      {` (${calDueDate} ${t('general:labels.day', {
                                        count: calDueDate,
                                      })})`}
                                    </span>
                                  );
                                })()}
                              </React.Fragment>
                            )}
                          </td>
                        </tr>
                        {asset.owned && (
                          <React.Fragment>
                            <tr>
                              <td>
                                <strong>{t('general:calibrations.labels.interval')}:</strong>
                              </td>
                              <td>{asset.calibration.interval}</td>
                            </tr>
                            <tr>
                              <td>
                                <strong>{t('general:calibrations.labels.planned_date')}:</strong>
                              </td>
                              {asset.calibration.planned_date && (
                                <td>
                                  <Moment
                                    date={asset.calibration.planned_date}
                                    format="MMMM DD, YYYY"
                                  />
                                </td>
                              )}
                            </tr>
                          </React.Fragment>
                        )}
                      </React.Fragment>
                    )}
                    <tr>
                      <td>
                        <strong>{t('general:assets.labels.calibration_status')}:</strong>
                      </td>
                      <td>{asset.calibration.status}</td>
                    </tr>
                    {asset.owned && asset.calibration.status !== 'Not Required' && (
                      <React.Fragment>
                        <tr>
                          <td>
                            <strong>{t('general:assets.labels.in_service_date')}:</strong>
                          </td>
                          <td>
                            {asset.calibration.in_service_date && (
                              <Moment
                                date={asset.calibration.in_service_date}
                                format="MMMM DD, YYYY"
                              />
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>{t('general:assets.labels.safety_tested')}:</strong>
                          </td>
                          <td>{asset.calibration.safety_tested}</td>
                        </tr>
                      </React.Fragment>
                    )}
                  </tbody>
                </table>

                {!asset.owned && (
                  <React.Fragment>
                    <h3>{t('general:assets.pricing')}</h3>
                    {this.renderPricingFields(asset)}
                  </React.Fragment>
                )}

                {!asset.owned && !!orderDatesRows.length ? (
                  <React.Fragment>
                    <h3>{t('general:assets.order_dates')}</h3>
                    <table>
                      <tbody>{orderDatesRows}</tbody>
                    </table>
                  </React.Fragment>
                ) : null}

                {!asset.owned && <DeliveryDetails />}

                <LocationDetails
                  title={t('general:assets.labels.location')}
                  deliveryAddress={isSold}
                  details={isSold ? asset.delivery : asset.location}
                />

                {asset.children !== false && <AssetRelationship />}

                {asset.parent && <AssetRelationship />}

                {asset.owned ? this.renderOwnedAssetTabs(t) : this.renderRentedAssetTabs(t, asset)}

                {asset.owned && <AdditionalInfo title="general:asset" details={asset} />}
              </div>

              <div className={styles['actions-panel']}>
                <StatusIndicator title={t('general:assets.labels.status')} status={asset.status} />
                {asset.user && asset.user.name && <AssignedTo assigned={asset.user.name} />}
                <ActionsList
                  actions={actionsListData}
                  data={asset}
                  disabled={Boolean(
                    asset && asset.contract && asset.contract.er_status === 'closed'
                  )}
                />

                {asset.owned && (
                  <Availability
                    title={t('general:labels.available_for')}
                    details={ownedAssetAvailability}
                    sidebar={true}
                  />
                )}

                <AuditAssetStatus asset={asset} />
              </div>
            </Paper>

            <Paper className={styles['activity-panel']}>
              {this.props.match.params.id && (
                <Activity
                  title={true}
                  url={generateUrl(assetUrls.activity, [this.props.match.params.id])}
                  refresh={this.props.actionDrawerStore.success}
                  allowFilter={true}
                />
              )}
            </Paper>
          </div>
        </PageWrapper>

        <ActionDrawerWrapper
          appendActionToURL={true}
          onActionCompleted={() => {
            /*overriding with empty fn*/
          }}
        >
          {
            {
              unassignUser: <UnassignUserFromAsset formData={asset} />,
              reassign: <ReassignAsset formData={[asset]} />,
              changeAssetLocation: <ChangeAssetLocation formData={asset} />,
              allocateToProject: <AllocateAssetToProject formData={[asset]} />,
              removeFromProject: <RemoveAssetFromProject formData={asset} />,
              changeLocation: <ChangeAssetLocation formData={asset} />,
              requestQuote: <RequestAssetQuote formData={asset} />,
              requestOwnership: <RequestAssetOwnership formData={asset} />,
              reportFault: <ReportAssetFault formData={asset} />,
              scheduleCalibration: <ScheduleAssetCalibration formData={asset} />,
              extendRental: <ExtendRental formData={asset} />,
              archive: <ArchiveAsset formData={asset} />,
              unarchive: <UnarchiveAsset formData={asset} />,
              assetAuditMulti: <SelfAuditMulti formData={[asset]} />,
            }[this.props.actionDrawerStore.currentActionDrawer]
          }
        </ActionDrawerWrapper>
      </React.Fragment>
    );
  }
}
